<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/store.js'
import { usePermissions } from '@/composables/usePermissions.js'
import { getMacros } from '@/api/api.js'
import { useToast } from '@/composables/useToast.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import RunMacroModal from '@/components/macros/RunMacroModal.vue'
import MdiPlay from '~icons/mdi/play'
import MdiPencil from '~icons/mdi/pencil'
import MdiRobot from '~icons/mdi/robot'

const { t } = useI18n()
const router = useRouter()
const store = useStore()
const toast = useToast()
const { isAdmin } = usePermissions()

const macros = ref([])
const loading = ref(false)
const loadError = ref(null)
const selectedMacro = ref(null)
const showRunModal = ref(false)

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const res = await getMacros({ is_active: true, page_size: 200 })
    macros.value = (res.results ?? res ?? []).filter((m) => m.is_active)
  } catch (e) {
    loadError.value = e?.data?.detail ?? t('automations.loadFailed')
  } finally {
    loading.value = false
  }
}

function openRun(macro) {
  selectedMacro.value = macro
  showRunModal.value = true
}

function onModalClose() {
  showRunModal.value = false
  selectedMacro.value = null
}

onMounted(load)
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="$t('automations.title')" :show-back="false">
      <template #actions>
        <button v-if="isAdmin" class="btn primary small" @click="router.push('/admin/macros')">
          {{ $t('automations.manageBtn') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Loading -->
    <div v-if="loading" class="list-stack">
      <div v-for="i in 4" :key="i" class="data-card animate-pulse">
        <div class="data-card__main">
          <div class="h-4 w-1/3 bg-white/10 rounded mb-2"></div>
          <div class="h-3 w-2/3 bg-white/5 rounded"></div>
        </div>
        <div class="data-card__actions">
          <div class="w-16 h-8 bg-white/10 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <template v-else-if="loadError">
      <EmptyState :message="loadError" variant="error" />
      <div class="flex justify-center mt-2">
        <button class="btn ghost small" @click="load">{{ $t('common.tryAgain') }}</button>
      </div>
    </template>

    <!-- Empty -->
    <template v-else-if="!macros.length">
      <div class="state-empty flex flex-col items-center gap-3 py-16">
        <MdiRobot class="w-10 h-10 text-[var(--color-muted)]" />
        <p class="text-[var(--color-muted)] m-0">{{ $t('automations.noAutomations') }}</p>
        <router-link v-if="isAdmin" to="/admin/macros/new" class="btn primary small">
          {{ $t('automations.createFirst') }}
        </router-link>
      </div>
    </template>

    <!-- List -->
    <div v-else class="list-stack">
      <div
        v-for="macro in macros"
        :key="macro.id"
        class="data-card"
      >
        <div class="data-card__main">
          <div class="data-card__name flex items-center gap-2">
            <MdiRobot class="w-4 h-4 text-[var(--color-primary)] flex-shrink-0" />
            {{ macro.name }}
          </div>
          <div v-if="macro.description" class="data-card__meta">
            <span class="text-[var(--color-muted)] text-sm">{{ macro.description }}</span>
          </div>
          <div class="data-card__meta mt-1">
            <span class="badge badge-muted">{{ $t('automations.steps', { n: macro.steps?.length ?? 0 }) }}</span>
          </div>
        </div>
        <div class="data-card__actions">
          <button
            v-if="isAdmin"
            class="btn-icon"
            :title="$t('automations.editInManager')"
            @click="router.push(`/admin/macros/${macro.id}`)"
          >
            <MdiPencil class="w-4 h-4" />
          </button>
          <button class="btn primary small" @click="openRun(macro)">
            <MdiPlay class="w-3.5 h-3.5" />
            {{ $t('automations.run') }}
          </button>
        </div>
      </div>
    </div>

    <RunMacroModal
      :show="showRunModal"
      :macro="selectedMacro"
      @close="onModalClose"
    />
  </div>
</template>
